import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Vue from 'vue'
import axios from 'axios'
import apiEndpoints from '@/constants/apiEndpoints'
import router from '../../../router'

export default function useTicketsList() {
  const toast = useToast()
  const refTicketListTable = ref(null)
  const loading = ref(false)

  const tableColumns = [
    { key: 'ticket_number', sortable: false, label: 'Ticket Number' },
    { key: 'function', sortable: false, label: 'Endpoint / Function' },
    { key: 'message', sortable: false, label: 'Message' },
    { key: 'status', sortable: false, label: 'Status' },
    { key: 'user', sortable: false, label: 'User' },
    { key: 'branch', sortable: false, label: 'Branch' },
    { key: 'time', sortable: false, label: 'Time' },
    { key: 'source', sortable: false, label: 'Source' },
    { key: 'actions', label: '' },
  ]

  const perPage = ref(10)
  const totalTickets = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const statusFilter = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refTicketListTable.value ? refTicketListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalTickets.value,
    }
  })

  const refetchData = () => {
    if (refTicketListTable.value) {
      refTicketListTable.value.refresh()
    }
  }

  watch([currentPage, perPage, searchQuery, statusFilter], () => {
    refetchData()
  })

  const fetchTickets = (ctx, callback) => {
    loading.value = true
    const to = perPage.value * (currentPage.value - 1)
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    const params = {
      limit: perPage.value,
      offset: to,
      search: searchQuery.value,
    }

    if (statusFilter.value) {
      params.status = statusFilter.value
    }

    axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_TICKETS}`, {
        headers,
        params,
      })
      .then(response => {
        if (response.data && response.data.data !== null) {
          totalTickets.value = response.data.data_count || 0
          callback(response.data.data)
        } else {
          callback([])
        }
        loading.value = false
      })
      .catch(e => {
        loading.value = false
        console.error(e)
        if (e.message === 'Request failed with status code 401') {
          router.push({ name: 'auth-login' })
        } else if (e.response && e.response.status === 404) {
          // Backend hides this route for non-super-admin users on purpose
          router.push({ name: 'error-404' })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching data list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
        callback([])
      })
  }

  const updateTicketStatus = (id, status, resolutionNote) => {
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    return axios.post(
      `${process.env.VUE_APP_API_URL}${apiEndpoints.API_TICKETS}/${id}/status`,
      {
        status,
        resolution_note: resolutionNote,
      },
      { headers },
    )
  }

  const resolveTicketStatusVariant = status => {
    if (status === 'open') return 'danger'
    if (status === 'in_progress') return 'warning'
    if (status === 'resolved') return 'success'
    if (status === 'ignored') return 'secondary'
    return 'primary'
  }

  const resolveTicketStatusLabel = status => {
    if (status === 'open') return 'Open'
    if (status === 'in_progress') return 'In Progress'
    if (status === 'resolved') return 'Resolved'
    if (status === 'ignored') return 'Ignored'
    return status
  }

  return {
    fetchTickets,
    updateTicketStatus,
    loading,
    tableColumns,
    perPage,
    currentPage,
    totalTickets,
    dataMeta,
    perPageOptions,
    searchQuery,
    refTicketListTable,
    refetchData,
    statusFilter,
    resolveTicketStatusVariant,
    resolveTicketStatusLabel,
  }
}
